import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import browserUtils from '../../utils/browser.utils';

@Component({
  selector: 'app-image-capture',
  templateUrl: './image-capture.component.html',
  styleUrls: ['./image-capture.component.scss'],
})
export class ImageCaptureComponent implements OnDestroy, AfterViewInit {
  @ViewChild('video') videoRef: ElementRef | undefined;
  @ViewChild('canvas') canvasRef: ElementRef | undefined;
  private canvas: any;
  private video: any;
  public videoCapable = true;
  private mediaStream: any;
  isMobile = false;

  @Input()
  redirectRoute: string | undefined;

  @Input()
  isSelf = false;

  @Output()
  imageCapture = new EventEmitter<{ image: string; appKey: string }>();

  @Output()
  cancel = new EventEmitter();
  isLoading = false;

  constructor() {
    this.isMobile = browserUtils.isMobile();
  }

  ngOnDestroy(): void {
    this.video.pause();

    this.unlockScreen();

    for (const track of this.mediaStream.getTracks()) {
      track.stop();
    }
    this.video.srcObject = null;
  }

  onCancel(event: Event): void {
    this.unlockScreen();
    this.cancel.emit(event);
  }

  async ngAfterViewInit(): Promise<void> {
    this.canvas = this.canvasRef?.nativeElement;
    this.video = this.videoRef?.nativeElement;

    await this.lockScreen();

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { ideal: 1280 },
            height: { ideal: 1024 },
            facingMode: this.isSelf ? 'user' : 'environment',
          },
        });
        this.videoCapable = true;
        this.video.srcObject = this.mediaStream;
        await this.video.play();
        this.canvas.width = this.video.videoWidth;
        this.canvas.height = this.video.videoHeight;
      } catch (e) {
        this.videoCapable = false;
      }
    }
  }

  canvasToUrl(): Promise<string> {
    return new Promise((resolve) => {
      this.canvas.toBlob((blob: Blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
      }, 'image/jpeg');
    });
  }

  async onSnap(): Promise<void> {
    if (this.canvasRef && this.videoRef) {
      this.canvasRef.nativeElement.height =
        this.videoRef.nativeElement.videoHeight;
      this.canvasRef.nativeElement.width =
        this.videoRef.nativeElement.videoWidth;
    }

    this.unlockScreen();

    const ctx: CanvasRenderingContext2D = this.canvas.getContext('2d');
    ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
    const url = await this.canvasToUrl();
    this.imageCapture.emit({image: url, appKey: ''});
  }

  async lockScreen() {
    if (browserUtils.isAndroid()) {
      await browserUtils.requestCameraAccess();

      await browserUtils.enterFullScreen();
      await browserUtils.lock(
        this.isSelf ? 'portrait-primary' : 'landscape-primary'
      );
    }
    return;
  }

  unlockScreen() {
    if (browserUtils.isAndroid()) {
      browserUtils.unlock();
      browserUtils.exitFullScreen();
    }
  }
}
